import { type FC } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Badge from '@segment/matcha/components/Badge'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CardBase, * as Card from '@segment/matcha/recipes/Card'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import getStartedBackground from './assets/GetStartedBackground.png'
import getStartedBackgroundWebp from './assets/GetStartedBackground.webp'

const copy = {
  heading: `Get started with good data`,
  subheading: `Achieving data maturity unlocks revenue for your business. Learn how with advice from our experts and industry analysts.`,
}

const data = [
  {
    url: `https://webinars.segment.com/segment/maximizing-customer-experience-with-a-strong-data-foundation?utm_source=good-data-hub&utm_medium=referral&utm_campaign=all_ic_2024-03-20-wbnr-maximizing-cx-with-a-data-foundation_20240522&utm_content=webinar`,
    badge: `Webinar`,
    heading: `Maximize customer experience with a strong data foundation`,
    subheading: `Hear from 451 Research on why companies that invest in their data deliver better outcomes and more revenue.`,
    buttonText: `Watch now`,
  },
  {
    url: `https://segment.com/learn/download/idc-marketshare-2022/?utm_source=good-data-hub&utm_medium=referral&utm_campaign=all_ic_2022-10-11-cnt-idc-marketshare-report_20240522&utm_content=report`,
    badge: `Analyst Report`,
    heading: `IDC CDP MarketShare Report`,
    subheading: `Dive into why IDC has ranked Twilio the #1 CDP in worldwide market shares for four consecutive years (2022, 2021, 2020, 2019).`,
    buttonText: `Download`,
  },
  {
    url: `https://webinars.segment.com/segment/how-to-win-cx-with-data-activation-forrester?utm_source=good-data-hub&utm_medium=referral&utm_campaign=all_ic_2024-04-16-wbnr-forrester-win-with-cx_20240522&utm_content=webinar`,
    badge: `Webinar`,
    heading: `How to win CX with differentiated data`,
    subheading: `Learn from Forrester on how to integrate your warehouse and tech stack to differentiate your real-time CX strategies.`,
    buttonText: `Watch now`,
  },
  {
    url: `https://webinars.segment.com/segment/twilio-segment-summer-school-customer-data-maturity-101-session-1-building-a-rock-solid-data-foundation-for-your-business?utm_source=good-data-hub&utm_medium=referral&utm_campaign=all_ic_2023-07-12-wbnr-cnt-cdm-intro-and-foundations_20240522&utm_content=webinar`,
    badge: `Webinar`,
    heading: `Building a rock-solid data foundation for your business`,
    subheading: `Learn how a strong customer data foundation can set your engagement strategy up for success.`,
    buttonText: `Watch now`,
  },
  {
    url: `https://webinars.segment.com/segment/from-insight-to-implementation-advancing-beyond-the-basics?utm_source=good-data-hub&utm_medium=referral&utm_campaign=all_ic_2023-07-26-wbnr-cdm-advancing%20_20240522&utm_content=webinar`,
    badge: `Webinar`,
    heading: `From insights to implementation: advancing beyond the basics`,
    subheading: `Start to add more complexity to your data strategy by activating your audiences and driving funnel conversion.`,
    buttonText: `Watch now`,
  },
  {
    url: `https://webinars.segment.com/segment/adapting-your-data-strategy-on-the-road-to-mastery?utm_source=good-data-hub&utm_medium=referral&utm_campaign=all_ic_2023-08-22-wbnr-cdm-adapting-your-data-strategy%20_20240522&utm_content=webinar`,
    badge: `Webinar`,
    heading: `Adapting your data strategy on the road to mastery`,
    subheading: `Become the leader in your category by exceeding customer expectations and staying ahead of the competition.`,
    buttonText: `Watch now`,
  },
]

const GetStarted: FC = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading variant="h2" tag="h2">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description>{copy.subheading}</CopyText.Description>
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <ColumnsWrapper>
            <ColumnTrack columns={1} overrides={{ md: 3 }}>
              {data.map((card, index) => (
                <CardBase key={index} href={getAbsoluteUrl(`${card.url}`, pageConfig)}>
                  <Card.Body>
                    <Card.Accessories>
                      <Card.AccessoriesRow>
                        <Badge variant="outlined">{card.badge}</Badge>
                      </Card.AccessoriesRow>
                    </Card.Accessories>
                    <Card.Copy>
                      <Card.Headline tag="h3">{card.heading}</Card.Headline>
                      <Card.Description>{card.subheading}</Card.Description>
                    </Card.Copy>
                    <Card.Action>
                      <ButtonText iconEnd={ChevronRightIcon}>{card.buttonText}</ButtonText>
                    </Card.Action>
                  </Card.Body>
                </CardBase>
              ))}
            </ColumnTrack>
          </ColumnsWrapper>
        </Section.Contents>
      </StyledSection>
      <Background>
        <picture>
          <source type="image/webp" srcSet={`${getStartedBackgroundWebp}`} />
          <Image src={getStartedBackground} alt="Hero Background Image" loading="eager" fill={true} />
        </picture>
      </Background>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
  background: transparent;
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    background: ${({ theme }) => theme.palette.twilio.mint[10]};
  }
`

const Background = styled.div`
  background: ${({ theme }) => theme.palette.twilio.mint[10]};
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  max-width: 100vw;
`

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    gap: 0;
  }
`

const ColumnsWrapper = styled.div`
  margin-top: 60px;
`

export default GetStarted
